import { motion } from "framer-motion";
import { Icon } from "@iconify/react";

const COMPLIANCE_FEATURES = [
    {
        icon: "mdi:certificate-outline",
        title: "Software certificado pela AGT",
        description:
            "Programa validado pela Administração Geral Tributária com licença oficial para emissão de documentos fiscais.",
    },
    {
        icon: "mdi:file-export-outline",
        title: "Exportação SAFT-AO",
        description:
            "Gere e exporte o ficheiro SAFT-AO conforme exigido pela legislação fiscal angolana, pronto para submissão.",
    },
    {
        icon: "mdi:shield-lock-outline",
        title: "Assinatura digital",
        description:
            "Todos os documentos fiscais são assinados digitalmente, garantindo autenticidade e integridade dos dados.",
    },
    {
        icon: "mdi:file-document-check-outline",
        title: "Documentos fiscais conformes",
        description:
            "Facturas, recibos, notas de crédito e guias de remessa em conformidade com o regime do IVA.",
    },
];

export default function AGTComplianceSection() {
    return (
        <section className="py-20 lg:py-28 bg-linear-to-br from-slate-900 via-slate-900 to-blue-950 overflow-hidden">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="grid lg:grid-cols-2 gap-16 items-center">
                    {/* Left - License badge & visual */}
                    <motion.div
                        initial={{ opacity: 0, x: -40 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        <div className="relative">
                            {/* Main compliance card */}
                            <div className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 p-8 text-center">
                                {/* AGT Shield */}
                                <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-linear-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg shadow-blue-500/20">
                                    <Icon
                                        icon="mdi:shield-check"
                                        width={40}
                                        className="text-white"
                                    />
                                </div>

                                <div className="text-sm text-blue-400 font-medium tracking-wider uppercase mb-2">
                                    Licença Oficial AGT
                                </div>

                                <div className="text-3xl sm:text-4xl font-bold text-white mb-2 font-mono tracking-wider">
                                    490/AGT/2024
                                </div>

                                <p className="text-white/40 text-sm mb-6">
                                    Software oficialmente certificado para
                                    <br />
                                    emissão de documentos fiscais em Angola
                                </p>

                                {/* Verification badge */}
                                <div className="inline-flex items-center gap-2 bg-blue-500/10 border border-blue-500/20 rounded-full px-4 py-2">
                                    <Icon
                                        icon="mdi:check-decagram"
                                        width={18}
                                        className="text-blue-400"
                                    />
                                    <span className="text-sm text-blue-400 font-medium">
                                        Software validado e certificado
                                    </span>
                                </div>
                            </div>

                            {/* Floating elements */}
                            <motion.div
                                className="absolute -top-3 -right-3 bg-white rounded-xl shadow-lg px-3 py-2 border border-gray-100"
                                animate={{ y: [0, -6, 0] }}
                                transition={{
                                    duration: 3,
                                    repeat: Infinity,
                                    ease: "easeInOut",
                                }}
                            >
                                <div className="flex items-center gap-2">
                                    <Icon
                                        icon="mdi:file-export-outline"
                                        width={16}
                                        className="text-blue-500"
                                    />
                                    <span className="text-xs font-semibold text-gray-700">
                                        SAFT-AO
                                    </span>
                                </div>
                            </motion.div>

                            <motion.div
                                className="absolute -bottom-3 -left-3 bg-white rounded-xl shadow-lg px-3 py-2 border border-gray-100"
                                animate={{ y: [0, 6, 0] }}
                                transition={{
                                    duration: 3.5,
                                    repeat: Infinity,
                                    ease: "easeInOut",
                                    delay: 0.5,
                                }}
                            >
                                <div className="flex items-center gap-2">
                                    <Icon
                                        icon="mdi:signature-freehand"
                                        width={16}
                                        className="text-blue-500"
                                    />
                                    <span className="text-xs font-semibold text-gray-700">
                                        Assinatura digital
                                    </span>
                                </div>
                            </motion.div>
                        </div>
                    </motion.div>

                    {/* Right - Content */}
                    <motion.div
                        initial={{ opacity: 0, x: 40 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.15 }}
                    >
                        <div className="flex items-center gap-2 mb-4">
                            <div className="h-px w-8 bg-blue-500" />
                            <span className="text-blue-400 text-sm font-medium tracking-wider uppercase">
                                Conformidade Fiscal
                            </span>
                        </div>

                        <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4 leading-tight">
                            Em total conformidade com a{" "}
                            <span className="text-blue-400">legislação angolana</span>
                        </h2>

                        <p className="text-white/50 mb-8 leading-relaxed">
                            O Logigate é um software oficialmente validado pela Administração
                            Geral Tributária (AGT) de Angola. Todos os documentos fiscais emitidos
                            cumprem integralmente os requisitos legais em vigor.
                        </p>

                        {/* Feature list */}
                        <div className="space-y-4">
                            {COMPLIANCE_FEATURES.map((feature, index) => (
                                <motion.div
                                    key={feature.title}
                                    initial={{ opacity: 0, x: 20 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    viewport={{ once: true }}
                                    transition={{
                                        duration: 0.3,
                                        delay: 0.2 + index * 0.08,
                                    }}
                                    className="flex gap-3"
                                >
                                    <div className="w-9 h-9 rounded-lg bg-blue-500/10 flex items-center justify-center shrink-0">
                                        <Icon
                                            icon={feature.icon}
                                            width={20}
                                            className="text-blue-400"
                                        />
                                    </div>
                                    <div>
                                        <h4 className="text-sm font-semibold text-white mb-0.5">
                                            {feature.title}
                                        </h4>
                                        <p className="text-xs text-white/40 leading-relaxed">
                                            {feature.description}
                                        </p>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}
